import React, { useState, useEffect, useRef } from 'react';
import { Button } from './button';
import { Input } from './input';
import { Label } from './label';
import { Card, CardContent } from './card';
import { Badge } from './badge';
import { ChevronDown, Plus, Search, X } from 'lucide-react';
import { useToast } from '../../hooks/use-toast';

interface CodelistItem {
  id?: number;
  code: string;
  name: string;
  description?: string;
  sort_order?: number;
  is_active?: boolean;
  is_system?: boolean;
}

interface CodelistConfig {
  allow_add: boolean;
  required: boolean;
  multiple: boolean;
  searchable: boolean;
}

interface SelectWithAddProps {
  label: string;
  value?: string | string[]; // Single value or array for multiple
  onChange: (value: string | string[]) => void;
  codelistType: string;
  placeholder?: string;
  disabled?: boolean;
  required?: boolean;
  className?: string;
  onAddItem?: (item: { code: string; name: string; description?: string }) => Promise<void>;
}

export const SelectWithAdd: React.FC<SelectWithAddProps> = ({
  label,
  value,
  onChange,
  codelistType,
  placeholder = "Select option...",
  disabled = false,
  required = false,
  className = "",
  onAddItem
}) => {
  const [items, setItems] = useState<CodelistItem[]>([]);
  const [config, setConfig] = useState<CodelistConfig>({
    allow_add: false,
    required: false,
    multiple: false,
    searchable: true
  });
  const [isOpen, setIsOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [loading, setLoading] = useState(false);
  const [showAddForm, setShowAddForm] = useState(false);
  const [newItem, setNewItem] = useState({ code: '', name: '', description: '' });
  const dropdownRef = useRef<HTMLDivElement>(null);
  const { toast } = useToast();

  // Load codelist items when component mounts
  useEffect(() => {
    loadItems();
  }, [codelistType]);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false);
        setShowAddForm(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const loadItems = async () => {
    try {
      setLoading(true);
      
      // Load items from API
      const response = await fetch(`/api/codelists/${codelistType}`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('auth_token')}`
        }
      });
      
      if (response.ok) {
        const data = await response.json();
        setItems(data.data || []);
      }

      // Load configuration
      const configResponse = await fetch(`/api/codelists/types`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('auth_token')}`
        }
      });
      
      if (configResponse.ok) {
        const configData = await configResponse.json();
        const typeConfig = configData.data?.find((t: any) => t.type === codelistType);
        if (typeConfig?.settings) {
          setConfig(typeConfig.settings);
        }
      }
    } catch (error) {
      console.error('Failed to load codelist:', error);
    } finally {
      setLoading(false);
    }
  };

  const filteredItems = items.filter(item => 
    !searchTerm || 
    item.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    item.code.toLowerCase().includes(searchTerm.toLowerCase()) ||
    item.description?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const selectedItems = config.multiple 
    ? items.filter(item => Array.isArray(value) && value.includes(item.code))
    : items.find(item => item.code === value);

  const handleSelect = (item: CodelistItem) => {
    if (config.multiple) {
      const currentValues = Array.isArray(value) ? value : [];
      const newValues = currentValues.includes(item.code)
        ? currentValues.filter(v => v !== item.code)
        : [...currentValues, item.code];
      onChange(newValues);
    } else {
      onChange(item.code);
      setIsOpen(false);
    }
  };

  const handleAddNew = async () => {
    if (!newItem.code.trim() || !newItem.name.trim()) {
      toast({
        title: "Error",
        description: "Code and name are required",
        variant: "destructive"
      });
      return;
    }

    try {
      if (onAddItem) {
        await onAddItem(newItem);
      } else {
        // Default API call
        const response = await fetch('/api/codelists/items', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${localStorage.getItem('auth_token')}`
          },
          body: JSON.stringify({
            codelist_type: codelistType,
            ...newItem
          })
        });

        if (!response.ok) {
          throw new Error('Failed to add item');
        }
      }

      // Reload items and select the new one
      await loadItems();
      onChange(config.multiple ? [...(Array.isArray(value) ? value : []), newItem.code] : newItem.code);
      
      // Reset form
      setNewItem({ code: '', name: '', description: '' });
      setShowAddForm(false);
      
      toast({
        title: "Success",
        description: "Item added successfully"
      });
    } catch (error) {
      console.error('Failed to add item:', error);
      toast({
        title: "Error",
        description: "Failed to add item",
        variant: "destructive"
      });
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && filteredItems.length > 0 && !showAddForm) {
      e.preventDefault();
      handleSelect(filteredItems[0]);
    } else if (e.key === 'Escape') {
      setIsOpen(false);
      setShowAddForm(false);
    }
  };

  const getDisplayValue = () => {
    if (config.multiple && Array.isArray(value)) {
      return value.length > 0 ? `${value.length} selected` : placeholder;
    } else if (!config.multiple && value) {
      const item = items.find(i => i.code === value);
      return item ? item.name : value;
    }
    return placeholder;
  };

  return (
    <div className={`relative ${className}`} ref={dropdownRef}>
      <Label className={required ? "after:content-['*'] after:ml-1 after:text-red-500" : ""}>
        {label}
      </Label>
      
      {/* Selected Values Display (Multiple mode) */}
      {config.multiple && Array.isArray(value) && value.length > 0 && (
        <div className="flex flex-wrap gap-1 mb-2">
          {value.map(val => {
            const item = items.find(i => i.code === val);
            return (
              <Badge key={val} variant="secondary" className="cursor-pointer" onClick={() => {
                if (!disabled) {
                  const newValues = value.filter(v => v !== val);
                  onChange(newValues);
                }
              }}>
                {item?.name || val}
                {!disabled && <X className="h-3 w-3 ml-1" />}
              </Badge>
            );
          })}
        </div>
      )}

      {/* Main Select Button */}
      <Button
        type="button"
        variant="outline"
        className="w-full justify-between"
        onClick={() => !disabled && setIsOpen(!isOpen)}
        disabled={disabled}
      >
        <span className="truncate">{getDisplayValue()}</span>
        <ChevronDown className="h-4 w-4 opacity-50" />
      </Button>

      {/* Dropdown */}
      {isOpen && !disabled && (
        <Card className="absolute top-full left-0 right-0 z-50 mt-1 max-h-80 overflow-hidden">
          <CardContent className="p-0">
            {/* Search Input */}
            {config.searchable && (
              <div className="p-3 border-b">
                <div className="relative">
                  <Search className="absolute left-3 top-3 h-4 w-4 text-muted-foreground" />
                  <Input
                    placeholder="Search..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    onKeyDown={handleKeyDown}
                    className="pl-9"
                    autoFocus
                  />
                </div>
              </div>
            )}

            {/* Items List */}
            <div className="max-h-48 overflow-y-auto">
              {loading ? (
                <div className="p-3 text-center text-muted-foreground">Loading...</div>
              ) : filteredItems.length > 0 ? (
                filteredItems.map(item => {
                  const isSelected = config.multiple 
                    ? Array.isArray(value) && value.includes(item.code)
                    : value === item.code;
                    
                  return (
                    <div
                      key={item.id || item.code}
                      className={`p-3 cursor-pointer hover:bg-muted border-b last:border-b-0 ${
                        isSelected ? 'bg-muted' : ''
                      }`}
                      onClick={() => handleSelect(item)}
                    >
                      <div className="flex items-center justify-between">
                        <div className="flex-1">
                          <div className="font-medium">{item.name}</div>
                          <div className="text-sm text-muted-foreground">{item.code}</div>
                          {item.description && (
                            <div className="text-xs text-muted-foreground mt-1">{item.description}</div>
                          )}
                        </div>
                        {isSelected && config.multiple && (
                          <Badge variant="default" className="ml-2">Selected</Badge>
                        )}
                      </div>
                    </div>
                  );
                })
              ) : (
                <div className="p-3 text-center text-muted-foreground">
                  {searchTerm ? 'No items found' : 'No items available'}
                </div>
              )}
            </div>

            {/* Add New Item Form */}
            {config.allow_add && (
              <div className="border-t">
                {!showAddForm ? (
                  <Button
                    type="button"
                    variant="ghost"
                    className="w-full justify-start"
                    onClick={() => setShowAddForm(true)}
                  >
                    <Plus className="h-4 w-4 mr-2" />
                    Add new item
                  </Button>
                ) : (
                  <div className="p-3 space-y-2">
                    <Input
                      placeholder="Code (e.g., art_28)"
                      value={newItem.code}
                      onChange={(e) => setNewItem(prev => ({ ...prev, code: e.target.value }))}
                    />
                    <Input
                      placeholder="Name (e.g., Article 28)"
                      value={newItem.name}
                      onChange={(e) => setNewItem(prev => ({ ...prev, name: e.target.value }))}
                    />
                    <Input
                      placeholder="Description (optional)"
                      value={newItem.description}
                      onChange={(e) => setNewItem(prev => ({ ...prev, description: e.target.value }))}
                    />
                    <div className="flex gap-2">
                      <Button
                        type="button"
                        size="sm"
                        onClick={handleAddNew}
                        disabled={!newItem.code.trim() || !newItem.name.trim()}
                      >
                        Add
                      </Button>
                      <Button
                        type="button"
                        variant="outline"
                        size="sm"
                        onClick={() => {
                          setShowAddForm(false);
                          setNewItem({ code: '', name: '', description: '' });
                        }}
                      >
                        Cancel
                      </Button>
                    </div>
                  </div>
                )}
              </div>
            )}
          </CardContent>
        </Card>
      )}
    </div>
  );
};
